// Comandos do agregado Fatura — o que a tela de Faturamento pode pedir: abrir
// rascunho, ajustar o período, ajustar valores, compensar peças, emitir,
// reabrir, excluir. Dado puro; o executor só despacha pras funções de domínio.
//
// Datas de emissão/reabertura vêm do `ctx.hoje` de quem executa (o servidor,
// em produção) — nunca do relógio do cliente.

import type { Snapshot } from "../domain/snapshot.ts";
import type { DataISO } from "../domain/datas.ts";
import {
  abrirFaturasEmMassa,
  adicionarAjuste,
  adicionarCompensacao,
  criarFaturaRascunho,
  editarPeriodoFatura,
  emitirFatura,
  excluirFatura,
  reabrirFatura,
  removerAjuste,
  removerCompensacao,
  type DadosCompensacao,
} from "../domain/faturamento.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirBooleano,
  exigirData,
  exigirTexto,
  lista,
  paraNumero,
  texto,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export interface PeriodoComando {
  de: string;
  ate: string;
}

export type ComandoFatura =
  | { tipo: "criar"; clienteId: string; periodo: PeriodoComando }
  | { tipo: "criarEmMassa"; clienteIds: string[]; periodo: PeriodoComando }
  | { tipo: "editarPeriodo"; faturaId: string; periodo: PeriodoComando }
  | { tipo: "excluir"; faturaId: string }
  | {
      tipo: "adicionarAjuste";
      faturaId: string;
      descricao: string;
      valorCentavos: number;
      tipoId?: string | null;
    }
  | { tipo: "removerAjuste"; faturaId: string; ajusteId: string }
  | ({ tipo: "adicionarCompensacao"; faturaId: string } & DadosCompensacao)
  | { tipo: "removerCompensacao"; faturaId: string; compensacaoId: string }
  | { tipo: "emitir"; faturaId: string; vencimento?: DataISO | null }
  | { tipo: "reabrir"; faturaId: string; justificativa: string }


export function executarComandoFatura(
  snapshot: Snapshot,
  cmd: ComandoFatura,
  ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "criar":
      return criarFaturaRascunho(snapshot, cmd.clienteId, cmd.periodo);
    case "criarEmMassa":
      // uma transição só; a tela reconstrói o resumo do diff do Snapshot
      return abrirFaturasEmMassa(snapshot, cmd.clienteIds, cmd.periodo).snapshot;
    case "editarPeriodo":
      return editarPeriodoFatura(snapshot, cmd.faturaId, cmd.periodo);
    case "excluir":
      return excluirFatura(snapshot, cmd.faturaId);
    case "adicionarAjuste":
      return adicionarAjuste(snapshot, cmd.faturaId, {
        descricao: cmd.descricao,
        valorCentavos: cmd.valorCentavos,
        tipoId: cmd.tipoId ?? null,
      });
    case "removerAjuste":
      return removerAjuste(snapshot, cmd.faturaId, cmd.ajusteId);
    case "adicionarCompensacao": {
      const { tipo: _tipo, faturaId, ...dados } = cmd;
      return adicionarCompensacao(snapshot, faturaId, dados);
    }
    case "removerCompensacao":
      return removerCompensacao(snapshot, cmd.faturaId, cmd.compensacaoId);
    case "emitir":
      return emitirFatura(snapshot, cmd.faturaId, ctx.hoje, cmd.vencimento);
    case "reabrir":
      return reabrirFatura(snapshot, cmd.faturaId, cmd.justificativa, ctx.hoje);
    default:
      return assertNunca(cmd);
  }
}

// --- Validação da forma do comando (fronteira HTTP) -------------------------
//
// Só a FORMA; as regras (período válido, fatura em rascunho, preço pendente…)
// ficam no domínio, que já devolve erro amigável. Helpers: `./_forma.ts`.

function validarPeriodo(valor: unknown): PeriodoComando {
  const o = comoObjeto(valor, "Período");
  return {
    de: exigirTexto(o.de, "periodo.de"),
    ate: exigirTexto(o.ate, "periodo.ate"),
  };
}

export function validarComandoFatura(valor: unknown): ComandoFatura {
  const c = comoObjeto(valor, "Comando de fatura");
  switch (c.tipo) {
    case "criar":
      return {
        tipo: "criar",
        clienteId: exigirTexto(c.clienteId, "clienteId"),
        periodo: validarPeriodo(c.periodo),
      };
    case "criarEmMassa":
      return {
        tipo: "criarEmMassa",
        clienteIds: lista(c.clienteIds, "clienteIds", (x, i) =>
          exigirTexto(x, `clienteIds[${i}]`),
        ),
        periodo: validarPeriodo(c.periodo),
      };
    case "editarPeriodo":
      return {
        tipo: "editarPeriodo",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        periodo: validarPeriodo(c.periodo),
      };
    case "excluir":
      return { tipo: "excluir", faturaId: exigirTexto(c.faturaId, "faturaId") };
    case "adicionarAjuste":
      return {
        tipo: "adicionarAjuste",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        descricao: typeof c.descricao === "string" ? c.descricao : "",
        valorCentavos: paraNumero(c.valorCentavos),
        tipoId: typeof c.tipoId === "string" && c.tipoId !== "" ? c.tipoId : null,
      };
    case "removerAjuste":
      return {
        tipo: "removerAjuste",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        ajusteId: exigirTexto(c.ajusteId, "ajusteId"),
      };
    case "adicionarCompensacao":
      return {
        tipo: "adicionarCompensacao",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        // número inválido vira NaN e o domínio recusa com mensagem amigável
        os: paraNumero(c.os),
        // vazio/ausente = sem data (a OS do sistema dá a dela)
        data: c.data == null || c.data === "" ? null : exigirData(c.data, "data"),
        motivo: texto(c.motivo),
        pecas: lista(c.pecas, "pecas", (x, i) => {
          const p = comoObjeto(x, `pecas[${i}]`);
          return {
            tipoId: exigirTexto(p.tipoId, `pecas[${i}].tipoId`),
            quantidade: paraNumero(p.quantidade),
          };
        }),
      };
    case "removerCompensacao":
      return {
        tipo: "removerCompensacao",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        compensacaoId: exigirTexto(c.compensacaoId, "compensacaoId"),
      };
    case "emitir":
      return {
        tipo: "emitir",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        // ausente = usa a sugestão do Cliente; null explícito = sem vencimento
        ...("vencimento" in c
          ? { vencimento: c.vencimento == null ? null : exigirData(c.vencimento, "vencimento") }
          : {}),
      };
    case "reabrir":
      return {
        tipo: "reabrir",
        faturaId: exigirTexto(c.faturaId, "faturaId"),
        justificativa: typeof c.justificativa === "string" ? c.justificativa : "",
      };
    default:
      throw new Error(`Tipo de comando de fatura desconhecido: ${String(c.tipo)}.`);
  }
}
